import { getComponentType, getReferenceDesign, isAction } from '../utils';
import { themeForValue } from '../themes';

export const dependencies = design => {
  const result = ['styled-components'];
  result.push('https://github.com/grommet/grommet/tarball/stable');
  result.push('https://github.com/grommet/grommet-icons/tarball/stable');
  const theme = themeForValue(design.theme);
  if (theme && (theme.packageUrl || theme.packageName))
    result.push(theme.packageUrl || theme.packageName);
  return result;
};

const router = firstPath => `
import { BrowserRouter as Router, Route, useHistory } from 'react-router-dom';

const RouterContext = React.createContext({ push: (path: string) => { } })

function Routes(props: { children: JSX.Element | JSX.Element[] }) {
  const history = useHistory();
  return (
    <RouterContext.Provider value={{ push: history.push }}>
      {props.children}
    </RouterContext.Provider>
  );
}
`;

const screenComponentName = ({ id, name }) =>
  name
    ? `${name.charAt(0).toUpperCase()}${name.replace(/\s/g, '').slice(1)}`
    : `Screen${id}`;

export const generateJSX = ({
  component,
  design,
  imports: importsArg,
  theme: themeArg,
}) => {
  const libraries = importsArg.filter(i => i.library).map(i => i.library);
  const imports = { Grommet: true };
  const iconImports = {};
  const theme = themeForValue(design.theme);
  const screenNames = {};
  let layers = {};
  let publishedTheme;

  const componentToJSX = ({ screen, id, indent = '  ', referenceDesign }) => {
    let result;
    const component = (referenceDesign || design).components[id];
    const type = getComponentType(libraries, component.type);
    if (component.type === 'designer.Icon' || component.type === 'Icon') {
      const { icon, ...rest } = component.props;
      iconImports[icon] = true;
      result = `${indent}<${icon} ${Object.keys(rest)
        .map(k => `${k}="${rest[k]}"`)
        .join(' ')} />`;
    } else if (
      component.type === 'designer.Repeater' ||
      component.type === 'Repeater'
    ) {
      const childId = component.children && component.children[0];
      result = childId
        ? (
          componentToJSX({
            screen,
            id: childId,
            indent: `${indent}  `,
            referenceDesign,
          }) + '\n'
        ).repeat(component.props.count)
        : '';
    } else if (
      component.type === 'designer.Reference' ||
      component.type === 'Reference'
    ) {
      const nextReferenceDesign = getReferenceDesign(importsArg, component);
      result = nextReferenceDesign
        ? componentToJSX({
          screen,
          id: component.props.component,
          indent,
          referenceDesign: nextReferenceDesign,
        })
        : (indent + '<' + design.components[component.props.component].name + ' />');
    } else {
      if (component.type === 'grommet.Layer') {
        layers[id] = true;
      }
      imports[type.name] = true;

      let children =
        (component.children &&
          component.children
            .map(cId =>
              componentToJSX({
                screen,
                id: cId,
                indent: `${indent}  `,
                referenceDesign,
              }),
            )
            .join('\n')) ||
        (component.text && `${indent}  ${component.text}`);
      if (children && children.length === 0) children = undefined;

      let nav;
      if (component.designProps && component.designProps.link) {
        const link = component.designProps.link;
        (Array.isArray(link) ? link : [link]).forEach(aLink => {
          if (aLink.component) {
            if (design.components[aLink.component]) {
              const target = design.components[aLink.component]
              nav = `setLayer(layer ? undefined : '${target.name}')`;
            }
          } else {
            const screen = design.screens[aLink.screen];
            if (screen) nav = `push("${screen.path}")`;
          }
        });
      }

      result = `${indent}<${type.name}${Object.keys(component.props)
        .filter(name => {
          const value = component.props[name];
          return !(
            (typeof value === 'object' && Object.keys(value).length === 0) ||
            value === '' ||
            value === undefined
          );
        })
        .map(name => {
          const value = component.props[name];
          // TODO: handle -component- props
          if (
            component.type === 'grommet.DropButton' &&
            name === 'dropContent'
          ) {
            return `  dropContent={(\n${componentToJSX({
              screen,
              id: value,
              indent: `${indent}  `,
              referenceDesign,
            })}\n${indent})}\n${indent}`;
          }
          if (typeof value === 'string') {
            if (name === 'icon') {
              iconImports[value] = true;
              return ` ${name}={<${value} />}`;
            }
            if (isAction(name)) {
              return ` ${name}={${value}}`;
            }
            return ` ${name}="${value}"`;
          }
          if (typeof value === 'boolean' && value) {
            return ` ${name}`;
          }
          return ` ${name}={${JSON.stringify(value)}}`;
        })
        .join('')}${nav ? ` onClick={() => ${nav}}` : ''}${component.type === 'Grommet' && theme ? ` theme={${theme}}` : ''
        }${children ? '' : ' /'}>${children ? `\n${children}\n${indent}</${type.name}>` : ''
        }`;
    }
    // TODO: library
    if (component.type === 'grommet.Layer') {
      result = `${indent}{layer === '${component.name}' && (
${result}
      )}`;
    }
    if (component.responsive && component.responsive.hide) {
      result = `${indent}{!${JSON.stringify(component.responsive.hide)}.includes(size) && (
${result}
      )}`;
    }
    return result;
  };

  if (component) {
    return componentToJSX({ id: component.id, indent: '' });
  } else {
    if (!theme) {
      publishedTheme = `const theme = grommet\n `;
    } else {
      if (theme.designerUrl) {
        // embed any theme from the designer, since code can't depend on it
        // directly
        publishedTheme = `const theme = ${JSON.stringify(themeArg, null, 2)}`;
      }
    }

    Object.keys(design.screens).forEach(sId => {
      const screen = design.screens[sId];
      screenNames[sId] = screenComponentName(screen);
    });

    const single = Object.keys(design.screens).length === 1;
    const screens = Object.keys(design.screens)
      .map(sKey => design.screens[sKey])
      .map(screen => {
        layers = {};
        const root = componentToJSX({
          screen,
          id: screen.root,
          indent: single ? '      ' : '    ',
        });
        return `${single ? 'const App =' : `const ${screenComponentName(screen)} =`
          } () => {
  const size = React.useContext(ResponsiveContext)
  ${!single ? 'const { push } = React.useContext(RouterContext)\n  ' : ''}${Object.keys(layers).length > 0
            ? 'const [layer, setLayer] = React.useState<string | undefined>(undefined)\n  const hide = () => setLayer(undefined)'
            : ''
          }
  return (${single ? `\n    <Grommet full theme={theme}>` : ''}
${root}
${single ? '    </Grommet>\n' : ''}  )
}

${single ? 'export default App;\n' : ''}
`;
      })
      .join('\n\n');

    return `import React${!single ? '' : ''} from 'react'
import { ${Object.keys(imports).join(', ')} } from 'grommet'
import { grommet, ResponsiveContext } from 'grommet'
${Object.keys(iconImports).length > 0
        ? `import { ${Object.keys(iconImports).join(', ')} } from 'grommet-icons'`
        : ''
      }
${(theme &&
        !theme.designerUrl &&
        `import { ${theme.name} as theme } from '${theme.packageName}'`) ||
      ''}
${!single ? router(design.screens[design.screenOrder[0]].path) : ''}
${publishedTheme || ''}
${screens}
${!single
        ? `
const App = () => (
  <Grommet full theme={theme}>
    <Router>
      <Routes>${Object.keys(design.screens)
          .map(id => design.screens[id])
          .map(
            screen =>
            (screen.path ? `\n        <Route exact path="${screen.path}" component={${screenComponentName(
              screen,
            )}} />` : ''),
          )
          .join('')}
      </Routes>
    </Router>
  </Grommet>
)

export default App;
`
        : ''
      }`;
  }
};
